import { useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import QRCode from 'qrcode';
import type { JournalEntry } from '../types/accounting';
import type { Organization } from './useOrganization';
import type { CurrencyCode } from '../config/currency';
import { useCurrency } from './useCurrency';
import { useJournalActivity } from './useActivityTracking';

interface JournalExportOptions {
  entries: JournalEntry[];
  organization: Organization | null;
  filters?: {
    startDate?: string;
    endDate?: string;
    journalType?: string;
    search?: string;
    status?: string;
    source?: string;
  };
  format: 'pdf' | 'excel' | 'csv';
  title?: string;
}

export function useJournalExport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { format: formatCurrencyHook } = useCurrency();
  const { logJournalExport } = useJournalActivity();

  const handleExport = async (options: JournalExportOptions) => {
    setLoading(true);
    setError(null);

    try {
      if (!options.entries || options.entries.length === 0) {
        throw new Error('Aucune écriture à exporter');
      }

      if (options.format === 'pdf') {
        await exportJournalToPDF(options, formatCurrencyHook);
        logJournalExport('PDF', getPeriodDescription(options.filters));
      } else if (options.format === 'excel') {
        await exportJournalToExcel(options);
        logJournalExport('Excel', getPeriodDescription(options.filters));
      } else {
        await exportJournalToCSV(options);
        logJournalExport('CSV', getPeriodDescription(options.filters));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Une erreur est survenue lors de l'export";
      setError(message);
      console.error("Journal export failed:", message, err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    handleExport
  };
}

const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = url;
  });
};

const getJournalTypeLabel = (type: string): string => {
  const labels = {
    sales: 'Ventes',
    purchases: 'Achats',
    bank: 'Banque',
    cash: 'Caisse',
    general: 'OD Diverses'
  };
  return labels[type as keyof typeof labels] || type;
};

const getStatusLabel = (status: string): string => {
  const labels = {
    draft: 'Brouillon',
    posted: 'Validé',
    cancelled: 'Annulé'
  };
  return labels[status as keyof typeof labels] || status;
};

const formatDateForFilename = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const getPeriodDescription = (filters?: JournalExportOptions['filters']) => {
  if (filters?.startDate && filters?.endDate) {
    return `${new Date(filters.startDate).toLocaleDateString('fr-FR')} - ${new Date(filters.endDate).toLocaleDateString('fr-FR')}`;
  } else if (filters?.startDate) {
    return `Depuis le ${new Date(filters.startDate).toLocaleDateString('fr-FR')}`;
  } else if (filters?.endDate) {
    return `Jusqu'au ${new Date(filters.endDate).toLocaleDateString('fr-FR')}`;
  }
  return 'Toutes les périodes';
};

async function exportJournalToPDF(options: JournalExportOptions, formatCurrency: (amount: number, currency?: CurrencyCode) => string): Promise<void> {
  const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
  doc.setLanguage('fr');

  const styles = {
    fontSize: {
      title: 16,
      subtitle: 12,
      heading: 10,
      normal: 9,
      small: 8,
      tiny: 7,
    },
    colors: {
      primary: '#111827',
      secondary: '#4B5563',
      accent: '#374151',
    },
    margins: {
      page: 15,
    },
  };

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = styles.margins.page;
  
  let y = margin;

  // --- HEADER AVEC LOGO ---
  // 1. QR Code (right aligned)
  const authString = JSON.stringify({
    org: options.organization?.registrationNumber || 'N/A',
    type: 'journal',
    date: new Date().toISOString(),
    generator: 'WANZO COMPTA',
    hash: Math.random().toString(36).substring(2, 15),
  });
  const encodedAuth = btoa(authString);
  const qrValue = `https://verify.wanzo.cd/auth/${encodedAuth}`;
  
  const qrSize = 22;
  const qrX = pageWidth - margin - qrSize;
  
  try {
    const qrCodeDataUrl = await QRCode.toDataURL(qrValue, {
        errorCorrectionLevel: 'H', type: 'image/png', margin: 1, width: 128
    });
    doc.addImage(qrCodeDataUrl, 'PNG', qrX, y, qrSize, qrSize);
    doc.setFontSize(styles.fontSize.tiny);
    doc.setTextColor(styles.colors.secondary);
    const authText = 'Authentification';
    const authTextWidth = doc.getTextWidth(authText);
    doc.text(authText, qrX + (qrSize - authTextWidth) / 2, y + qrSize + 3);
  } catch (err) {
    console.error("Failed to generate QR code", err);
  }

  // 2. Organization Info & Logo (left aligned)
  let textX = margin;
  let logoHeight = 0;

  if (options.organization?.logo) {
    try {
      const logoImg = await loadImage(options.organization.logo);
      const aspectRatio = logoImg.width / logoImg.height;
      logoHeight = 20;
      let logoWidth = logoHeight * aspectRatio;
      if (logoWidth > 40) {
          logoWidth = 40;
          logoHeight = logoWidth / aspectRatio;
      }
      doc.addImage(logoImg, 'PNG', margin, y, logoWidth, logoHeight);
      textX += logoWidth + 5;
    } catch (e) {
      console.error("Could not load organization logo", e);
      // Fallback : créer un logo simple avec les initiales
      const companyName = options.organization?.name || 'ENTREPRISE';
      const initials = companyName.split(' ').map(word => word[0]).join('').substring(0, 3);
      
      doc.setFillColor(52, 73, 85); // Couleur de fond
      doc.rect(margin, y, 20, 20, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      const initialsWidth = doc.getTextWidth(initials);
      doc.text(initials, margin + (20 - initialsWidth) / 2, y + 13);
      
      logoHeight = 20;
      textX += 25;
    }
  }

  // Informations de l'entreprise
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(styles.fontSize.heading);
  doc.setTextColor(styles.colors.primary);
  doc.text((options.organization?.name || 'ENTREPRISE').toUpperCase(), textX, y + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(styles.fontSize.small);
  doc.setTextColor(styles.colors.secondary);

  const ids = [
      options.organization?.registrationNumber ? `RCCM: ${options.organization.registrationNumber}` : null,
      options.organization?.taxId ? `ID. NAT: ${options.organization.taxId}` : null,
  ].filter(Boolean).join(' | ');
  if (ids) doc.text(ids, textX, y + 10);

  const address = [
      options.organization?.city,
      options.organization?.country,
      options.organization?.phone || options.organization?.entrepreneurPhone,
  ].filter(Boolean).join(' | ');
  if (address) doc.text(address, textX, y + 15);

  y = Math.max(y + logoHeight, y + qrSize) + 8;

  // --- TITRE DU RAPPORT ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(styles.fontSize.title);
  doc.setTextColor(styles.colors.primary);
  const title = options.title || 'JOURNAL DES ÉCRITURES COMPTABLES';
  const titleWidth = doc.getTextWidth(title);
  doc.text(title, (pageWidth - titleWidth) / 2, y);
  y += 10;

  // --- INFORMATIONS DU RAPPORT ---
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(styles.fontSize.small);
  doc.setTextColor(styles.colors.secondary);

  const reportInfo = [
    `Date d'édition: ${new Date().toLocaleDateString('fr-FR')}`,
    `Période: ${options.filters?.startDate ? `Du ${new Date(options.filters.startDate).toLocaleDateString('fr-FR')}` : 'Depuis le début'} ${options.filters?.endDate ? `au ${new Date(options.filters.endDate).toLocaleDateString('fr-FR')}` : "à aujourd'hui"}`,
    `Nombre d'écritures: ${options.entries.length}`,
    `Monnaie de tenue: ${options.organization?.currency || 'CDF'}`
  ];

  reportInfo.forEach(info => {
    const infoWidth = doc.getTextWidth(info);
    doc.text(info, (pageWidth - infoWidth) / 2, y);
    y += 4;
  });

  y += 8;

  // --- LIGNE DE SÉPARATION ---
  doc.setDrawColor(styles.colors.accent);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  // --- TABLEAU DES ÉCRITURES ---
  const totalDebit = options.entries.reduce((sum, entry) => sum + entry.totalDebit, 0);
  const totalCredit = options.entries.reduce((sum, entry) => sum + entry.totalCredit, 0);

  const tableData = options.entries.map(entry => [
    new Date(entry.date).toLocaleDateString('fr-FR'),
    entry.reference,
    getJournalTypeLabel(entry.journalType),
    entry.description.length > 30 ? entry.description.substring(0, 30) + '...' : entry.description,
    getStatusLabel(entry.status),
    formatCurrency(entry.totalDebit, options.organization?.currency as CurrencyCode || 'CDF'),
    formatCurrency(entry.totalCredit, options.organization?.currency as CurrencyCode || 'CDF')
  ]);

  // Ajouter la ligne de total
  tableData.push([
    '', '', '', '', 'TOTAUX',
    formatCurrency(totalDebit, options.organization?.currency as CurrencyCode || 'CDF'),
    formatCurrency(totalCredit, options.organization?.currency as CurrencyCode || 'CDF')
  ]);

  autoTable(doc, {
    head: [['Date', 'Référence', 'Journal', 'Description', 'Statut', 'Débit', 'Crédit']],
    body: tableData,
    startY: y,
    theme: 'grid',
    styles: {
      fontSize: styles.fontSize.small,
      cellPadding: 2,
      textColor: styles.colors.primary,
    },
    headStyles: {
      fillColor: [243, 244, 246],
      textColor: [55, 65, 81],
      fontStyle: 'bold',
    },
    columnStyles: {
      0: { cellWidth: 18 },  // Date
      1: { cellWidth: 22 },  // Référence
      2: { cellWidth: 18 },  // Journal
      3: { cellWidth: 55 },  // Description
      4: { cellWidth: 18 },  // Statut
      5: { cellWidth: 30, halign: 'right', fontStyle: 'normal' },  // Débit
      6: { cellWidth: 30, halign: 'right', fontStyle: 'normal' },  // Crédit
    },
    didParseCell: function(data: any) {
      // Mise en forme spéciale pour la ligne de total
      if (data.row.index === tableData.length - 1) {
        data.cell.styles.fillColor = [31, 41, 55];
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = 'bold';
      }
      
      // Alignement spécial pour les colonnes de montants (Débit et Crédit)
      if (data.column.index === 5 || data.column.index === 6) {
        data.cell.styles.halign = 'right';
        data.cell.styles.fontFamily = 'courier'; // Police monospace pour les chiffres
      }
    },
    margin: { left: margin, right: margin },
  });

  // --- RÉSUMÉ FINANCIER ---
  // @ts-ignore - jspdf-autotable ajoute lastAutoTable à l'objet doc
  const finalY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 10 : 200;
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(styles.fontSize.normal);
  doc.setTextColor(styles.colors.primary);
  
  const summaryY = finalY;
  doc.text('RÉSUMÉ FINANCIER', margin, summaryY);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(styles.fontSize.small);
  
  const summary = [
    `Total Débit: ${formatCurrency(totalDebit, options.organization?.currency as CurrencyCode || 'CDF')}`,
    `Total Crédit: ${formatCurrency(totalCredit, options.organization?.currency as CurrencyCode || 'CDF')}`,
    `Équilibre: ${totalDebit === totalCredit ? '✓ Correct' : '⚠ Déséquilibré'}`,
  ];
  
  let summaryTextY = summaryY + 6;
  summary.forEach(line => {
    doc.text(line, margin, summaryTextY);
    summaryTextY += 5;
  });

  // --- PIED DE PAGE ---
  const footerY = pageHeight - 25;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(styles.fontSize.tiny);
  doc.setTextColor(styles.colors.secondary);
  
  doc.text('Système de Gestion Comptable WANZO COMPTA', margin, footerY);
  doc.text(`Document généré automatiquement le ${new Date().toLocaleString('fr-FR')}`, margin, footerY + 4);
  doc.text('Conforme au référentiel comptable SYSCOHADA | République Démocratique du Congo', margin, footerY + 8);
  
  // Numéro de page
  const pageNum = `Page 1 sur 1`;
  const pageNumWidth = doc.getTextWidth(pageNum);
  doc.text(pageNum, pageWidth - margin - pageNumWidth, footerY);

  // Télécharger le PDF
  doc.save(`journal-comptable-${formatDateForFilename()}.pdf`);
}

async function exportJournalToExcel(options: JournalExportOptions): Promise<void> {
  const workbook = XLSX.utils.book_new();
  
  // Préparer les données
  const data = options.entries.map(entry => ({
    'Date': new Date(entry.date).toLocaleDateString('fr-FR'),
    'Référence': entry.reference,
    'Type Journal': getJournalTypeLabel(entry.journalType),
    'Description': entry.description,
    'Statut': getStatusLabel(entry.status),
    'Débit': entry.totalDebit,
    'Crédit': entry.totalCredit,
    'TVA': entry.totalVat || 0,
    'Source': entry.source === 'agent' ? 'ADHA AI' : 'Manuel'
  }));

  // Ajouter les totaux
  const totalDebit = options.entries.reduce((sum, entry) => sum + entry.totalDebit, 0);
  const totalCredit = options.entries.reduce((sum, entry) => sum + entry.totalCredit, 0);
  
  data.push({
    'Date': '',
    'Référence': '',
    'Type Journal': '',
    'Description': '',
    'Statut': 'TOTAUX',
    'Débit': totalDebit,
    'Crédit': totalCredit,
    'TVA': 0,
    'Source': ''
  });

  const worksheet = XLSX.utils.json_to_sheet(data);
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Journal Comptable');
  
  XLSX.writeFile(workbook, `journal-comptable-${formatDateForFilename()}.xlsx`);
}

async function exportJournalToCSV(options: JournalExportOptions): Promise<void> {
  const headers = [
    'Date',
    'Référence',
    'Type Journal',
    'Description',
    'Statut',
    'Débit',
    'Crédit',
    'TVA',
    'Source'
  ];

  const rows = options.entries.map(entry => [
    entry.date,
    entry.reference,
    getJournalTypeLabel(entry.journalType),
    `"${entry.description}"`,
    getStatusLabel(entry.status),
    entry.totalDebit.toString(),
    entry.totalCredit.toString(),
    (entry.totalVat || 0).toString(),
    entry.source === 'agent' ? 'ADHA AI' : 'Manuel'
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `journal-comptable-${formatDateForFilename()}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
